
import Layout from '../components/Layout';
import { Link } from 'react-router-dom';
import { Shield, Bot, MessageSquare, Users, Sparkles, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';

const Features = () => {
  const featuresData = [
    {
      icon: <Shield className="w-10 h-10 text-fembot-pink" />,
      title: "Powerful Moderation",
      description: "Effectively manage your server with advanced moderation tools including auto-mod, warning system, and customizable responses.",
      link: "/moderation",
      buttonText: "Explore Moderation Tools"
    },
    {
      icon: <Bot className="w-10 h-10 text-fembot-pink" />,
      title: "AI Capabilities",
      description: "Leverage advanced AI for conversation and smart responses (available with premium tiers).",
      link: "/ai-features",
      buttonText: "Explore AI Features",
      premium: true
    },
    {
      icon: <MessageSquare className="w-10 h-10 text-fembot-pink" />,
      title: "Engagement Tools",
      description: "Keep your server active with rock paper scissors game, polls, and fun interaction commands like hug, kiss, and snuggle.",
      link: "/engagement",
      buttonText: "Explore Engagement Tools",
      premium: true
    },
    {
      icon: <Shield className="w-10 h-10 text-fembot-pink" />,
      title: "Security Tools",
      description: "Protect your server with raid protection, verification systems, and anti-phishing measures.",
      link: "/security-tools",
      buttonText: "Explore Security Tools"
    }
  ];

  return (
    <Layout>
      <div className="py-24 bg-gradient-to-b from-white to-pink-50 dark:from-gray-800 dark:to-gray-900">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 dark:text-white">
              Powerful <span className="text-fembot-pink">Features</span>
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Everything you need for an engaging and well-managed Discord community
            </p>
            <p className="text-sm text-fembot-pink mt-3 italic">
              Some features are still in development and may not be available at this point in time
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {featuresData.map((feature, index) => (
              <Card key={index} className="overflow-visible border-0 shadow-lg hover:shadow-xl transition-all duration-300 relative">
                {feature.premium && (
                  <div className="absolute -top-3 -right-3 z-10">
                    <div className="inline-flex items-center p-1.5 rounded-full bg-gradient-to-r from-yellow-400 to-amber-600">
                      <div className="bg-white dark:bg-gray-800 rounded-full px-3 py-1">
                        <div className="flex items-center space-x-1 rounded-full text-xs font-semibold text-amber-700 dark:text-amber-400">
                          <Crown className="h-3 w-3" />
                          <span>Premium</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <CardHeader className="bg-gray-50 dark:bg-gray-800 pb-4">
                  <div className="bg-pink-50 dark:bg-gray-700 p-3 rounded-lg inline-block">
                    {feature.icon}
                  </div>
                  <CardTitle className="mt-4 text-2xl">{feature.title}</CardTitle>
                  <CardDescription className="mt-2 text-base">{feature.description}</CardDescription>
                </CardHeader>
                <CardContent className="pt-4">
                  <ul className="space-y-2">
                    {feature.title === "Powerful Moderation" && (
                      <>
                        <li className="flex items-center">
                          <div className="w-2 h-2 bg-fembot-pink rounded-full mr-2"></div>
                          <span>Auto-moderation system</span>
                        </li>
                        <li className="flex items-center">
                          <div className="w-2 h-2 bg-fembot-pink rounded-full mr-2"></div>
                          <span>Warning and mute management</span>
                        </li>
                      </>
                    )}
                    {feature.title === "AI Capabilities" && (
                      <>
                        <li className="flex items-center">
                          <div className="w-2 h-2 bg-fembot-pink rounded-full mr-2"></div>
                          <span>Natural conversation abilities</span>
                        </li>
                        <li className="flex items-center">
                          <div className="w-2 h-2 bg-fembot-pink rounded-full mr-2"></div>
                          <span>Content generation</span>
                        </li>
                      </>
                    )}
                    {feature.title === "Engagement Tools" && (
                      <>
                        <li className="flex items-center">
                          <div className="w-2 h-2 bg-fembot-pink rounded-full mr-2"></div>
                          <span>Rock Paper Scissors game</span>
                        </li>
                        <li className="flex items-center">
                          <div className="w-2 h-2 bg-fembot-pink rounded-full mr-2"></div>
                          <span>Interactive polls</span>
                        </li>
                      </>
                    )}
                    {feature.title === "Security Tools" && (
                      <>
                        <li className="flex items-center">
                          <div className="w-2 h-2 bg-fembot-pink rounded-full mr-2"></div>
                          <span>Raid protection</span>
                        </li>
                        <li className="flex items-center">
                          <div className="w-2 h-2 bg-fembot-pink rounded-full mr-2"></div>
                          <span>Member verification</span>
                        </li>
                      </>
                    )}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button 
                    className="w-full bg-fembot-pink hover:bg-fembot-dark-pink text-white"
                    asChild
                  >
                    <Link to={feature.link}>{feature.buttonText}</Link>
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold dark:text-white">
                Ready to enhance your Discord server?
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Add Fembot to your server today and explore all these features for free.
              </p>
            </div>
            <div className="flex justify-center">
              <Button 
                className="bg-gradient-to-r from-fembot-pink to-fembot-dark-pink hover:from-fembot-dark-pink hover:to-fembot-pink text-white px-8 py-6 text-lg"
                onClick={() => window.open("https://discord.com/oauth2/authorize?client_id=1324844843670835240", "_blank")}
              >
                Add to Discord
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Features;
